import { dataFactoryInstance } from "@/v2/models/base-data-object.js";
import { Column, Record } from "@/v2/models/bo-datatable.js";

export const boDatatableNewMixin = {
    methods: {
        boGenericHandler(defaultArgs, eventName, ...args) {
            switch (eventName) {
                case "column_sequence_change":
                    this.boChangeColumnSequence(defaultArgs.columns, ...args);
                    return;
                case "tableRow_sequence_change":
                    this.boChangeTableRowSequence(defaultArgs.records, ...args);

                    return;
                case "delete_multiple":
                    if (defaultArgs.delete_multiple != null) {
                        defaultArgs.delete_multiple(...args);
                    } else {
                        this.boDatatableDelete(defaultArgs.records, ...args);
                    }
                    return;
                case "select_multiple_records":
                    this.boDatatableSelectRecord(defaultArgs.records, ...args);
                    return;
                case "selected_single_record":
                    this.boDatatableSelectSingleRecord(defaultArgs.records, ...args);
                    return;
                default:
                    return;
            }
        },

        boDatatableModify(records, key, columnKey, event) {
            if (records[key][columnKey] == null) {
                this.$set(records[key], columnKey, event);
            } else {
                records[key][columnKey] = event;
            }
        },

        boDatatableDelete(records, keys) {
            keys.forEach((key) => {
                this.$delete(records, key);
            });
        },

        /**
         * Processes columns upon mount
         * @function
         * @param {Object} columns Initial columns passed by config
         * @returns {object} Object containing Records
         */
        boDatatableProcessColumns(columns) {
            let processedColumns = {};

            Object.values(columns).forEach((column) => {
                processedColumns[column.id] = dataFactoryInstance.construct(Column, column);
            });

            return processedColumns;
        },

        boDatatableProcessColumnsWithStringKey(columns) {
            let processedColumns = {};

            for (const key in columns) {
                if (columns.hasOwnProperty(key)) {
                    processedColumns[key] = dataFactoryInstance.construct(Column, columns[key]);
                }
            }

            return processedColumns;
        },

        boDatatableSelectRecord(records, recordKey, value = null) {
            if (value != null) {
                records[recordKey].checked = value;
            } else {
                records[recordKey].checked = !records[recordKey].checked;
            }
        },

        boDatatableSelectSingleRecord(records, recordKey) {
            if (records[recordKey].checked) {
                records[recordKey].checked = !records[recordKey].checked;
            } else {
                Object.values(records).forEach((record) => {
                    record.checked = false;
                });
                records[recordKey].checked = true;
            }
        },

        boDatatableUnselectRecords(records) {
            Object.values(records).forEach((record) => {
                record.checked = false;
            });

            return records;
        },

        boChangeColumnSequence(columns, columnSequence) {
            let newColumns = {};

            for (const column of columnSequence) {
                newColumns[column.id] = column;
            }

            this.boAssign(columns, newColumns);
        },

        boChangeTableRowSequence(records, recordSequence) {
            let newRecords = {};

            for (const record of recordSequence) {
                newRecords[record.id] = record;
            }

            this.boAssign(records, newRecords);
        },

        /**
         * Resets an object's keys and fills object again with data from another source.
         * @function
         * @param {Object} target Target object.
         * @param {Object} source Source data.
         */
        boAssign(target, source) {
            for (const key in target) {
                this.$delete(target, key);
            }

            for (const key in source) {
                this.$set(target, key, source[key]);
            }

            return target;
        },

        /**
         * Creates a collection of Record objects and assigns their order index to be sorted later.
         * @function
         * @param {Object} target Target object to assign the collection to.
         * @param {Array} source Source data.
         * @param {String} [primaryKey='id'] Primary key of the source data.
         */
        boProcessRecords(target, source, primaryKey = "id") {
            let temp = {};
            let iterable = source;

            if (!(iterable instanceof Array)) {
                iterable = Object.values(iterable);
            }

            iterable.forEach((item, index) => {
                let record = item;
                if (!(item instanceof Record)) {
                    record = new Record(record);
                }
                record.order = index;

                temp[record[primaryKey]] = record;
            });

            return this.boAssign(target, temp);
        },

        boFetchRecords(
            target,
            targetPagination,
            payload = null,
            success = null,
            failure = null,
            url = null,
            callback = null
        ) {
            const getItems = this.boFetchItemsCallback(url, callback);

            getItems(
                payload,
                (data, pagination) => {
                    this.boProcessRecords(target, data);
                    Object.assign(targetPagination, pagination);
                    if (success) success(this.records);
                },
                failure
            );
        },

        boFetchItemsCallback(url = null, callback = null) {
            if (callback) {
                return callback;
            }

            return (payload = null, success = null, failure = null) =>
                this.getData(
                    url,
                    payload,
                    (response) => {
                        success(response.data.data, response.data.meta);
                    },
                    failure
                );
        },

        boAddRecords(existingRecords, records, primaryKey = "id", callback = null) {
            this.boClearRecords(existingRecords);
            return this.boProcessRecords(
                existingRecords,
                Object.values(records).map((record) => {
                    if (record && typeof record.getFreshRecord == "function") {
                        return callback ? callback(record.getFreshRecord()) : record.getFreshRecord();
                    } else {
                        return record;
                    }
                }),
                primaryKey
            );
        },

        boClearRecords(records) {
            for (let i in records) {
                if (records.hasOwnProperty(i)) {
                    this.$delete(records, i);
                }
            }
        },
    },
};

export default boDatatableNewMixin;
